import React, { useEffect, useState } from 'react';
import { Link } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import { shuffle } from 'lodash';

import Layout from '../components/layout';
import Head from '../components/head';

import styles from './listpage.module.scss';

const HomePage = ({
  pageContext: { totalVehicles, featuredVehicles },
}) => {
  const [orderedVehicles, setOrderedVehicles] = useState([]);

  useEffect(() => {
    setOrderedVehicles(shuffle(featuredVehicles).slice(0, 20));
  }, [featuredVehicles]);

  return (
    <Layout>
      <Head title="Hollingdean Parking Home" />

      <main className={styles.listpage}>
        <div className={styles.content}>
          <div className={styles.lead}>
            <p>
              In 2023, residents of Hollingdean were asked by council members to
              provide proof that the uncontrolled parking situation in their
              neighbourhoods had become untenable; to demonstrate displacement
              from the adjoining Fiveways parking zones, and to show that
              non-residents really were parking here in huge numbers.
            </p>
            <p>This is that proof.</p>
          </div>
          <p>
            In response, residents launched a grassroots survey, initially
            conducted over a three-month period. During that time, they
            systematically documented the{' '}
            <Link to="/gallery/non-residential">non-resident</Link>,{' '}
            <Link to="/gallery/displaced/">displaced</Link>,{' '}
            <Link to="/gallery/commuters/">commuter</Link>,{' '}
            <Link to="/gallery/abandoned/">abandoned</Link>, tourist (or a
            combination thereof) <Link to="/gallery/cars/">cars</Link>,{' '}
            <Link to="/gallery/taxis/">taxis</Link>,{' '}
            <Link to="/gallery/vans/">vans</Link>, and{' '}
            <Link to="/gallery/camper-vans/">camper vans</Link> using the area
            as a free car park.
          </p>
          <p>In just those first three months:</p>
          <ul>
            <li>
              <strong>Over 2,000 reports</strong> were submitted,
            </li>
            <li>
              By <strong>more than 80 individual residents,</strong>
            </li>
            <li>
              Covering <strong>over 300 separate vehicles.</strong>
            </li>
          </ul>
          <p>
            What began as a short-term project has since become an ongoing
            community effort. Many vehicles are tracked month by month,
            providing clear evidence of long-term stays, abandonment, or
            repeated non-resident use. Even today, some streets can see over a
            dozen new reports a week.
          </p>
          <p>
            For the sake of fairness and accuracy, no vehicle appears on this
            site until it has received at least three independent reports. This
            does, however, mean that many known non-resident vehicles haven't
            been published yet. At present, there are more than{' '}
            <strong>{Math.floor(totalVehicles * 1.2)}</strong> vehicles{' '}
            <Link to="/gallery">in our gallery</Link>.
          </p>
          <p>
            This data demonstrates an ongoing strain on Hollingdean, where
            residents face dangerous parking, increased traffic, noise, and a
            daily battle to park anywhere near their own homes. It shows that on
            some streets, anywhere up to 90% of parked vehicles at any one time
            belong to non-residents. While some are left long-term, blocking
            precious road space, many are commuters and others drawn to the area
            by the lack of restrictions, increasing congestion and degrading
            both air quality and quality of life for local residents.
          </p>
        </div>

        <h2 className={styles.featuredtitle}>Featured vehicles</h2>
        <p>
          Below is just a small collection of some of the problems we face in
          our community. View more in our{' '}
          <Link to="/gallery/">full gallery</Link>.
        </p>
        <ul className={styles.grid}>
          {orderedVehicles.map(
            (
              {
                make,
                model,
                colour,
                numberPlate,
                gridImage: {
                  optimizedLocalFile: {
                    childImageSharp: { gatsbyImageData },
                  },
                },
              },
              i
            ) => (
              <li key={`featuredVehicles-${i}`} className={styles.item}>
                <Link
                  to={`/gallery/${numberPlate
                    .replace(' ', '-')
                    .toLowerCase()}/`}
                  className={styles.link}
                  aria-label={`View ${numberPlate}, a ${colour} ${make} ${model} parked in Hollingdean`}
                >
                  <GatsbyImage
                    image={gatsbyImageData}
                    alt={`${numberPlate}, a ${colour} ${make} ${model} parked in Hollingdean`}
                    placholder="dominantColor"
                    loading="lazy"
                  />
                  <h2 className={styles.numberplate}>{numberPlate}</h2>
                </Link>
              </li>
            )
          )}
        </ul>
        <div className={styles.buttonWrap}>
          <Link to="/gallery/" className={styles.button}>
            View full gallery
          </Link>
        </div>
      </main>
    </Layout>
  );
};

export default HomePage;
